import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { toast } from 'react-toastify';
import { useDispatch } from "react-redux";
import { setUser } from "../store/authSlice";
import { syncUserProfileFromApi } from "../services/syncUserProfile";
import { prefetchApartmentBrandLogo } from "../services/apartmentLogo";
import { persistInviteDomainFromProfile } from "../services/inviteDomain";
import LoginHeroImage from "../components/LoginHeroImage";
import {
  PROPERTY_INFO_KEY,
  ownerRestrictedHomePath
} from "../utils/propertyAccess";

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // API Base URL - Update this with your actual API endpoint
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}`, {
        action: 'login',
        email: email,
        password: password
      });

      if (response.data.status === 'success') {
        const data = response.data.data || {};
        const {
          id,
          email: userEmail,
          name,
          role,
          access,
          adminId,
          propertyInfo,
          property,
          profileImage,
          image,
          avatar
        } = data;
        const loginImage = String(profileImage || image || avatar || "").trim();

        // Store data in localStorage
        localStorage.setItem('userid', id);
        localStorage.setItem('email', userEmail);
        localStorage.setItem('name', name);
        localStorage.setItem("monthlyMaintenanceAmount", property.monthlyMaintenanceAmount);
        localStorage.setItem('role', role);
        if (loginImage) {
          localStorage.setItem('profileImage', loginImage);
        } else {
          localStorage.removeItem('profileImage');
        }

        if (access && Array.isArray(access)) {
          localStorage.setItem('access', JSON.stringify(access));
        } else {
          localStorage.removeItem('access');
        }
        if (adminId != null && adminId !== '') {
          localStorage.setItem('adminId', String(adminId));
        } else {
          localStorage.removeItem('adminId');
        }
        if (propertyInfo != null && String(propertyInfo).trim() !== "") {
          localStorage.setItem(PROPERTY_INFO_KEY, String(propertyInfo));
        } else {
          localStorage.removeItem(PROPERTY_INFO_KEY);
        }

        persistInviteDomainFromProfile(data);

        // Store user in Redux (Header etc.)
        dispatch(
          setUser({
            userid: id,
            email: userEmail,
            name,
            role,
            access: Array.isArray(access) ? access : [],
            adminId: adminId != null ? String(adminId) : '',
            profileImage: loginImage
          })
        );

        // Login often omits profileImage — hydrate from getProfile before entering app
        await syncUserProfileFromApi(dispatch, id);

        // Prefetch + cache apartment brand logo so Sidebar shows it instantly (no default flash)
        const logoAdminId =
          adminId != null && String(adminId).trim() !== ""
            ? String(adminId).trim()
            : String(id);
        await prefetchApartmentBrandLogo(logoAdminId);

        toast.success(response.data.message || 'Login successful');
        navigate(ownerRestrictedHomePath(role, propertyInfo));
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response) {
        toast.error(error.response.data?.message || 'Login failed. Please try again.');
      } else if (error.request) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Mobile / tablet hero — same skyline as desktop */}
      <div className="login-image-section mobile-image">
        <div className="image-wrapper">
          <LoginHeroImage />
        </div>
      </div>

      {/* Left Panel - Login Form */}
      <div className="login-left-panel">
        <div className="login-content">
          {/* Header Section */}
          <div className="login-header">
            <img src="/Assets/Images/Adukkumadi-Logos.png" alt="Logo" className="login-logo-image" />
          </div>

          {/* Login Form Section */}
          <div className="login-form-section">
            <h1 className="login-title">Login</h1>
            
            <form onSubmit={handleSubmit} className="login-form">
              {/* Email Input */}
              <div className="input-wrapper">
                <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.5 6.66667L10 11.6667L17.5 6.66667M3.33333 15H16.6667C17.5871 15 18.3333 14.2538 18.3333 13.3333V6.66667C18.3333 5.74619 17.5871 5 16.6667 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667V13.3333C1.66667 14.2538 2.41286 15 3.33333 15Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-input"
                  required
                />
              </div>

              {/* Password Input */}
              <div className="input-wrapper">
                <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.8333 9.16667H4.16667C3.24619 9.16667 2.5 9.91286 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91286 16.7538 9.16667 15.8333 9.16667Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.83333 9.16667V5.83333C5.83333 4.72876 6.27232 3.66894 7.05372 2.88754C7.83512 2.10614 8.89493 1.66667 9.99999 1.66667C11.1051 1.66667 12.1649 2.10614 12.9463 2.88754C13.7277 3.66894 14.1667 4.72876 14.1667 5.83333V9.16667" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 10C2.5 10 5.83333 4.16667 10 4.16667C14.1667 4.16667 17.5 10 17.5 10C17.5 10 14.1667 15.8333 10 15.8333C5.83333 15.8333 2.5 10 2.5 10Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 10C2.5 10 5.83333 4.16667 10 4.16667C14.1667 4.16667 17.5 10 17.5 10C17.5 10 14.1667 15.8333 10 15.8333C5.83333 15.8333 2.5 10 2.5 10Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M4 16L16 4" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>

              {/* Forget Password Link */}
              <div className="forget-password-wrapper">
                <a href="/forgot" onClick={(e) => { e.preventDefault(); navigate('/forgot'); }} className="forget-password-link">
                  Forget password?
                </a>
              </div>

              {/* Next Button */}
              <button type="submit" className="login-next-btn" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
            
          </div>
        </div>
      </div>

      {/* Right Panel - Image (Desktop) */}
      <div className="login-right-panel desktop-image">
        <div className="image-wrapper">
          <LoginHeroImage />
        </div>
      </div>

      {/* Footer - Centered across entire page */}
      <div className="login-footer">
        <p className="login-footer-text">Developed by <a href="https://instatenders.in" target="_blank" rel="noopener noreferrer" className="footer-link">instatenders.in</a></p>
      </div>
    </div>
  );
};

export default Login;
